// src/pages/Admin/Dojos/CreateDojoModal.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import ReactDOM from "react-dom";
import {
  FaSave,
  FaTimes,
  FaImage,
  FaBuilding,
  FaBook,
  FaMapMarkerAlt,
  FaListOl,
  FaUpload,
  FaTrashAlt,
} from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { addDojo } from "../../../services/dojoServices";
import { uploadDojoPhoto } from "../../../services/dojoPhotoServices";
import { toast } from "react-toastify";

const CreateDojoModal = ({ isActive, onClose, refreshDojos }) => {
  const [formData, setFormData] = useState({
    dojo_nom: "",
    dojo_discipline: "",
    dojo_adresse: "",
    dojo_photoURL: "",
    dojo_ordre: "",
  });

  const defaultDojo = "/img/dojo_placeholder.jpg";
  const fallbackAvatar = "/img/dojo_placeholder.jpg";

  const [previewSrc, setPreviewSrc] = useState(defaultDojo);

  // Fichier local (optionnel)
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(""); // ObjectURL
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const dialogRef = useRef(null);

  // Ne convertir en URL absolue que les fichiers côté serveur (/uploads/...)
  const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    // déjà absolu, data URL ou blob
    if (
      /^https?:\/\//i.test(s) ||
      s.startsWith("data:") ||
      s.startsWith("blob:")
    )
      return s;
    // chemins d'assets front (ex: /img/...): on laisse tel quel
    if (!s.startsWith("/uploads")) return s;
    // fichiers uploadés côté API
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  // Ouverture : aperçu, focus, ESC + lock scroll
  useEffect(() => {
    if (!isActive) return;

    // Si pas de fichier sélectionné : aperçu = URL (ou placeholder)
    if (!file) {
      setPreviewSrc(
        formData.dojo_photoURL?.trim() ? formData.dojo_photoURL : defaultDojo
      );
    }

    const t = setTimeout(() => {
      dialogRef.current?.querySelector("input[name='dojo_nom']")?.focus();
    }, 0);

    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onEsc = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onEsc);

    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onEsc);
      root.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Préview en fonction du fichier choisi vs URL
  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setFilePreview(url);
      setPreviewSrc(url);
      return () => URL.revokeObjectURL(url);
    } else {
      // Revenir à l’aperçu URL si le fichier est retiré
      setPreviewSrc(
        formData.dojo_photoURL?.trim() ? formData.dojo_photoURL : defaultDojo
      );
      setFilePreview("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const resetForm = () => {
    setFormData({
      dojo_nom: "",
      dojo_discipline: "",
      dojo_adresse: "",
      dojo_photoURL: "",
      dojo_ordre: "",
    });
    setFile(null);
    setFilePreview("");
    setPreviewSrc(defaultDojo);
    setUploadProgress(0);
    setSubmitting(false);
  };

  const handleClose = () => {
    onClose?.();
    resetForm();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "dojo_photoURL" && !file) {
      const v = value.trim();
      setPreviewSrc(v ? v : defaultDojo);
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) {
      setFile(null);
      return;
    }
    // Simple contrôle type/poids côté front
    const okType = /^image\/(png|jpe?g|webp)$/i.test(f.type);
    if (!okType) {
      toast.error("Format d'image non supporté. Utilisez PNG / JPG / WEBP.");
      e.target.value = "";
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo).");
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const clearFile = () => {
    setFile(null);
    const input = dialogRef.current?.querySelector("input[type='file']");
    if (input) input.value = "";
  };

  // Payload Dojo (création)
  const buildPayload = () => {
    // Si un fichier a été choisi, on peut envoyer le placeholder (la vraie photo sera téléversée après création)
    const photo = file
      ? defaultDojo
      : formData.dojo_photoURL?.trim()
      ? formData.dojo_photoURL.trim()
      : defaultDojo;

    return {
      dojo_nom: formData.dojo_nom.trim(),
      dojo_discipline: formData.dojo_discipline.trim(),
      dojo_adresse: formData.dojo_adresse.trim(),
      dojo_photoURL: photo,
      dojo_ordre:
        String(formData.dojo_ordre || "").trim() === ""
          ? 0
          : Number(formData.dojo_ordre),
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    try {
      setSubmitting(true);
      setUploadProgress(0);

      const payload = buildPayload();
      // Validations simples
      if (!payload.dojo_nom) throw new Error("Le nom du dojo est requis.");
      if (!payload.dojo_discipline)
        throw new Error("La discipline est requise.");
      if (!payload.dojo_adresse) throw new Error("L'adresse est requise.");
      if (!Number.isFinite(payload.dojo_ordre))
        throw new Error("L'ordre d'affichage doit être un nombre.");

      // 1) Création du dojo
      const created = await addDojo(payload); // on suppose { id, ... } en retour
      const dojoId = created?.id;
      toast.success("Dojo créé avec succès");

      // 2) Si fichier local fourni -> uploadDojoPhoto
      if (dojoId && file) {
        try {
          await uploadDojoPhoto(dojoId, file, {
            format: "webp",
            onUploadProgress: (evt) => {
              if (!evt.total) return;
              const p = Math.round((evt.loaded / evt.total) * 100);
              setUploadProgress(p);
            },
          });
          toast.success("Photo du dojo téléversée");
        } catch (err) {
          console.error("Upload photo dojo échoué:", err);
          toast.error("Dojo créé, mais l'upload de la photo a échoué.");
        }
      }

      handleClose();
      refreshDojos?.();
    } catch (error) {
      console.error("Erreur lors de la création du dojo", error);
      toast.error(
        error?.response?.data ||
          error?.message ||
          "Erreur lors de la création du dojo"
      );
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  if (!isActive) return null;

  // --- PORTAL ---
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Créer un dojo"
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-3xl flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95 max-h-[92dvh] sm:max-h-[92dvh]"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {/* Header sticky */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 sm:px-6 flex items-center justify-between">
          <div>
            <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight">
              Créer un Nouveau Dojo
            </h3>
            <p className="text-white/90 text-xs sm:text-sm">
              Renseignez les informations du dojo.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white"
            aria-label="Fermer"
            type="button"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Corps scrollable */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] gap-4 sm:gap-6">
            {/* Aperçu + URL + Fichier */}
            <div className="flex md:flex-col gap-4 md:gap-5">
              {/* Aperçu visuel */}
              <div className="relative self-start">
                <img
                  src={toAbsolute(previewSrc)}
                  alt="Aperçu du dojo"
                  onError={(e) => {
                    e.currentTarget.src = fallbackAvatar;
                  }}
                  className="h-24 w-24 sm:h-28 sm:w-28 rounded-xl object-cover shadow ring-2 ring-white dark:ring-slate-800"
                />
                <span className="absolute -bottom-1 -right-1 inline-flex items-center justify-center h-7 w-7 rounded-full bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 shadow ring-2 ring-white dark:ring-slate-900">
                  <FaImage className="text-[12px]" />
                </span>
              </div>

              {/* Champ URL photo */}
              <div className="flex-1">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  URL de la photo
                </label>
                <input
                  type="url"
                  name="dojo_photoURL"
                  value={formData.dojo_photoURL}
                  onChange={handleChange}
                  placeholder="https://…/dojo.jpg (facultatif)"
                  inputMode="url"
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  PNG/JPG/WebP recommandé. Format horizontal conseillé (ex:
                  1280×720).
                </p>

                {/* OU : Upload d'un fichier local */}
                <div className="mt-3">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-200 flex items-center gap-2">
                    <FaUpload /> Téléverser un fichier (optionnel)
                  </label>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={handleFileChange}
                    className="mt-1 block w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-violet-600 file:text-white hover:file:bg-violet-700"
                  />
                  {file && (
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <span className="text-xs text-slate-600 dark:text-slate-300 truncate">
                        {file.name} ({Math.round(file.size / 1024)} Ko)
                      </span>
                      <button
                        type="button"
                        onClick={clearFile}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200"
                        title="Retirer le fichier"
                      >
                        <FaTrashAlt /> Retirer
                      </button>
                    </div>
                  )}

                  {/* Barre de progression upload */}
                  {submitting && file && (
                    <div className="mt-2 w-full h-2 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                      <div
                        className="h-full bg-violet-600 transition-all"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  )}
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Si une URL et un fichier sont fournis, le <b>fichier</b>{" "}
                    sera utilisé. Taille max : 10 Mo.
                  </p>
                </div>
              </div>
            </div>

            {/* Champs principaux */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              {/* Nom du dojo */}
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Nom du dojo <span className="text-pink-600">*</span>
                </label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaBuilding />
                  </span>
                  <input
                    type="text"
                    name="dojo_nom"
                    value={formData.dojo_nom}
                    onChange={handleChange}
                    required
                    placeholder="Dojo du Plaisir"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>

              {/* Discipline */}
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Discipline <span className="text-pink-600">*</span>
                </label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaBook />
                  </span>
                  <input
                    type="text"
                    name="dojo_discipline"
                    value={formData.dojo_discipline}
                    onChange={handleChange}
                    required
                    placeholder="Judo, Ju-jitsu, etc."
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>

              {/* Adresse */}
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Adresse <span className="text-pink-600">*</span>
                </label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaMapMarkerAlt />
                  </span>
                  <input
                    type="text"
                    name="dojo_adresse"
                    value={formData.dojo_adresse}
                    onChange={handleChange}
                    required
                    placeholder="2 Rue de la République, 78370 Plaisir"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>

              {/* Ordre d’affichage */}
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Ordre d’affichage <span className="text-pink-600">*</span>
                </label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaListOl />
                  </span>
                  <input
                    type="number"
                    name="dojo_ordre"
                    value={formData.dojo_ordre}
                    onChange={handleChange}
                    required
                    min="0"
                    step="1"
                    placeholder="0"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer sticky */}
          <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 mt-6">
            <div className="pointer-events-none h-6 bg-gradient-to-t from-white/95 dark:from-slate-900/95 to-transparent" />
            <div className="pointer-events-auto bg-white/95 dark:bg-slate-900/95 border-t border-slate-200/70 dark:border-slate-800/70 px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
              <Button
                text="Annuler"
                icon={FaTimes}
                onClick={handleClose}
                type="button"
                className="!rounded-xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 gap-2"
                disabled={submitting}
              />
              <Button
                text={submitting ? "Enregistrement..." : "Enregistrer"}
                icon={FaSave}
                type="submit"
                className="!rounded-xl gap-2"
                disabled={submitting}
              />
            </div>
          </div>
        </form>
      </div>

      {/* Styles locaux : placeholders + scrollbar */}
      <style>{`
        input::placeholder { color: #94a3b8; }
        .dark input::placeholder { color: #94a3b8; opacity: .7; }

        /* Scrollbar esthétique pour le corps de modale */
        .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar { width: 10px; }
        .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,.5);
          border-radius: 9999px;
          border: 3px solid transparent;
          background-clip: content-box;
        }
        .dark .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,.35);
        }
        .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-track { background: transparent; }

        html { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
      `}</style>
    </div>,
    document.body
  );
};

export default CreateDojoModal;
